import clsx from 'clsx'
import React, { useId, useMemo, useRef } from 'react'

import { Body } from './Body'
import { MarketplaceCardContext } from './context'
import { Description } from './Description'
import { Discount } from './Discount'
import { OriginalPrice } from './OriginalPrice'
import { Price } from './Price'
import { PriceDetails } from './PriceDetails'
import { Thumbnail } from './Thumbnail'
import { Title } from './Title'

interface MarketplaceCardProps {
  children: React.ReactNode;
  href?: string;
  onClick?: React.MouseEventHandler<HTMLLIElement>;
  target?: string;
}

export function MarketplaceCard ({
  children,
  href,
  onClick,
  target,
}: MarketplaceCardProps) {
  const titleRef = useRef<HTMLAnchorElement>(null)
  const titleId = useId()
  const isClickable = !!href || !!onClick
  const contextValue = useMemo(
    () => ({ href, isClickable, titleId, titleRef, target }),
    [href, isClickable, titleId, target]
  )

  return (
    <li
      aria-labelledby={titleId}
      className={clsx('flex flex-col shadow rounded-2xl', {
        'cursor-pointer hover:shadow-md focus-within:shadow-md outline outline-2 outline-offset-2 outline-transparent has-[:focus-visible]:outline-thatch-500 transition': isClickable
      })}
      onClick={(event) => {
        /**
         * For screen readers, only the title should be a link so that the
         * user isn't overwhelmed by the entire text of the card being read for
         * each link. But for mouse users we want the convenience of the
         * entire card being clickable. So when the card is clicked, we trigger
         * a click on the title link. That click will propagate back up to this
         * handler so we also need to avoid calling the component's `onClick`
         * callback twice.
         */
        if (href && event.target !== titleRef.current) {
          titleRef.current?.click()
        } else {
          onClick?.(event)
        }
      }}
    >
      <MarketplaceCardContext.Provider value={contextValue}>
        {children}
      </MarketplaceCardContext.Provider>
    </li>
  )
}

MarketplaceCard.Body = Body
MarketplaceCard.Description = Description
MarketplaceCard.Discount = Discount
MarketplaceCard.PriceDetails = PriceDetails
MarketplaceCard.OriginalPrice = OriginalPrice
MarketplaceCard.Price = Price
MarketplaceCard.Thumbnail = Thumbnail
MarketplaceCard.Title = Title
